import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  DeleteOutlined,
  LoadingOutlined,
  CopyOutlined,
  GlobalOutlined,
  CodeOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { servicesService, ordersService } from '../../services';
import { ServiceEvent, ServiceEventResponse } from '../../services/services.service';
import { ServiceStatusBadge, ConfirmationModal, DetailPageHeader, SecurityRulesTab, CustomDomainsTab, MachineSpecsCard, ServiceLogo, HeaderActionButton, HeaderActionMenu } from '../../components/shared';
import { getServiceAge } from '../../utils/serviceHelpers';
import { useNotificationContext } from '../../contexts/NotificationContext';
import MonitoringTab from '../ServiceDetail/components/MonitoringTab';
import LazyServiceEventsList from '../../components/shared/LazyServiceEventsList';

const mapLevelToEventType = (level?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!level) return 'info';
  const l = level.toUpperCase();
  if (l === 'SUCCESS') return 'success';
  if (l === 'ERROR') return 'error';
  if (l === 'WARNING') return 'warning';
  return 'info';
};

const mapStackStatusToEventType = (s?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!s) return 'info';
  const u = s.toUpperCase();
  if (u.includes('COMPLETE') && !u.includes('FAILED')) return 'success';
  if (u.includes('FAILED') || u.includes('ROLLBACK')) return 'error';
  return 'info';
};

const mapServiceEventsToUI = (events: ServiceEventResponse[]): ServiceEvent[] =>
  events.map((e, i) => {
    let timestamp: Date;
    try { timestamp = new Date(e.createdAt); } catch { timestamp = new Date(); }
    const eventType = e.level ? mapLevelToEventType(e.level) : mapStackStatusToEventType(e.stackStatus);
    const message = e.message || 'No message';
    const isKubiyEvent = e.source?.toLowerCase() === 'kubiy';
    const msgLower = message.toLowerCase();
    const isDeletionEvent = msgLower.includes('deleted') || msgLower.includes('eliminado');
    return {
      id: e.PK || e.SK || `event-${i}`,
      timestamp,
      type: eventType,
      message,
      details: e.description || e.statusReason || undefined,
      source: e.source || 'System',
      isKubiyEvent,
      isDeletionEvent,
    };
  });

const isCreatingStatus = (s?: string): boolean => {
  if (!s) return false;
  return ['deploying', 'initializing', 'stacking', 'creating'].includes(s.toLowerCase());
};


const WordPressDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useNotificationContext();

  const [service, setService] = useState<any>(null);
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<ServiceEvent[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'connection' | 'events' | 'monitoring' | 'security' | 'domains'>('overview');
  const [isProcessing, setIsProcessing] = useState(false);

  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [adminCredentials, setAdminCredentials] = useState<{ email?: string; password?: string } | null>(null);
  const [dbCredentials, setDbCredentials] = useState<{ username?: string; password?: string } | null>(null);
  const [loadingCreds, setLoadingCreds] = useState(false);
  const [adminPasswordVisible, setAdminPasswordVisible] = useState(false);
  const [dbPasswordVisible, setDbPasswordVisible] = useState(false);

  const hasFetchedRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) { setError('No service ID provided'); setLoading(false); return; }
      if (hasFetchedRef.current === id) return;
      hasFetchedRef.current = id;
      try {
        setLoading(true);
        setError(null);
        const svc: any = await servicesService.getById(id);
        const createdAt = svc.createdAt ? (typeof svc.createdAt === 'string' ? new Date(svc.createdAt) : svc.createdAt) : new Date();
        const updatedAt = svc.updatedAt ? (typeof svc.updatedAt === 'string' ? new Date(svc.updatedAt) : svc.updatedAt) : new Date();
        setService({ ...svc, status: svc.status?.toLowerCase(), createdAt, updatedAt });
        if (svc.orderId) {
          try {
            const orderData = await ordersService.getById(svc.orderId);
            setOrder(orderData);
          } catch { /* ignore */ }
        }
      } catch (err: any) {
        setError(err.message || 'Error loading service');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    if (!id || !service || !isCreatingStatus(service.status)) return;
    const interval = setInterval(async () => {
      try {
        const svc: any = await servicesService.getById(id);
        const status = svc.status?.toLowerCase();
        const createdAt = svc.createdAt ? (typeof svc.createdAt === 'string' ? new Date(svc.createdAt) : svc.createdAt) : new Date();
        const updatedAt = svc.updatedAt ? (typeof svc.updatedAt === 'string' ? new Date(svc.updatedAt) : svc.updatedAt) : new Date();
        setService({ ...svc, status, createdAt, updatedAt });
        if (!isCreatingStatus(status)) {
          clearInterval(interval);
          const eventsData = await servicesService.getEvents(id);
          setEvents(mapServiceEventsToUI(eventsData));
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [id, service?.status]);

  const handleRefresh = async () => {
    if (!id) return;
    try {
      setIsRefreshing(true);
      const svc: any = await servicesService.getById(id);
      const createdAt = svc.createdAt ? (typeof svc.createdAt === 'string' ? new Date(svc.createdAt) : svc.createdAt) : new Date();
      const updatedAt = svc.updatedAt ? (typeof svc.updatedAt === 'string' ? new Date(svc.updatedAt) : svc.updatedAt) : new Date();
      setService({ ...svc, status: svc.status?.toLowerCase(), createdAt, updatedAt });
      const eventsData = await servicesService.getEvents(id);
      setEvents(mapServiceEventsToUI(eventsData));
      success('Service updated');
    } catch (err: any) {
      showError(err.message || 'Error refreshing');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      setIsDeleting(true);
      await servicesService.delete(id);
      success('Service deleted');
      navigate('/wordpress');
    } catch (err: any) {
      showError(err.message || 'Error deleting service');
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const copyText = (text: string, label?: string) => {
    navigator.clipboard.writeText(text);
    success(`${label || 'Value'} copied`);
  };

  const handleOpenCredentialsModal = () => {
    setAdminCredentials(null);
    setDbCredentials(null);
    setAdminPasswordVisible(false);
    setDbPasswordVisible(false);
    setCredentialsModalOpen(true);
  };

  const handleFetchCredentials = async () => {
    if (!id) return;
    setLoadingCreds(true);
    try {
      const [adminData, dbData] = await Promise.allSettled([
        servicesService.getWordPressCredentials(id),
        servicesService.getWordPressDbCredentials(id),
      ]);
      if (adminData.status === 'fulfilled') {
        setAdminCredentials({ email: adminData.value.email, password: adminData.value.password });
      }
      if (dbData.status === 'fulfilled') {
        setDbCredentials({ username: dbData.value.username, password: dbData.value.password });
      }
      if (adminData.status === 'rejected' && dbData.status === 'rejected') {
        showError('Error fetching credentials');
      }
    } catch (err: any) {
      showError(err?.message || 'Error fetching credentials');
    } finally {
      setLoadingCreds(false);
    }
  };

  const handleConnect = () => { if (id) navigate(`/wordpress/terminal/${id}`); };

  const handleStart = async () => {
    if (!service || !id) return;
    setIsProcessing(true);
    try {
      const response = await servicesService.start(id);
      if (response) {
        const finalService = await servicesService.monitorServiceStatus(id, ['running', 'stopped', 'deleted'], (updatedService) => {
          const svc = updatedService as any;
          setService((prev: any) => ({ ...prev, ...svc, status: (svc.status || 'starting')?.toLowerCase() }));
        });
        const svc = finalService as any;
        setService({ ...svc, status: svc.status?.toLowerCase(), createdAt: new Date(svc.createdAt), updatedAt: new Date(svc.updatedAt) });
        success('Service started');
      }
    } catch (err: any) {
      showError(err?.message || 'Error starting service');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStop = async () => {
    if (!service || !id) return;
    setIsProcessing(true);
    try {
      const response = await servicesService.stop(id);
      if (response) {
        const finalService = await servicesService.monitorServiceStatus(id, ['stopped', 'running', 'deleted'], (updatedService) => {
          const svc = updatedService as any;
          setService((prev: any) => ({ ...prev, ...svc, status: (svc.status || 'stopping')?.toLowerCase() }));
        });
        const svc = finalService as any;
        setService({ ...svc, status: svc.status?.toLowerCase(), createdAt: new Date(svc.createdAt), updatedAt: new Date(svc.updatedAt) });
        success('Service stopped');
      }
    } catch (err: any) {
      showError(err?.message || 'Error stopping service');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRestart = async () => {
    if (!service || !id) return;
    setIsProcessing(true);
    try {
      const response = await servicesService.restart(id);
      if (response) {
        const finalService = await servicesService.monitorServiceStatus(id, ['running', 'stopped', 'deleted'], (updatedService) => {
          const svc = updatedService as any;
          setService((prev: any) => ({ ...prev, ...svc, status: (svc.status || 'restarting')?.toLowerCase() }));
        });
        const svc = finalService as any;
        setService({ ...svc, status: svc.status?.toLowerCase(), createdAt: new Date(svc.createdAt), updatedAt: new Date(svc.updatedAt) });
        success('Service restarted');
      }
    } catch (err: any) {
      showError(err?.message || 'Error restarting service');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingOutlined className="text-4xl text-blue-600" spin />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <button onClick={() => navigate('/wordpress')} className="mb-4 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
          <ArrowLeftOutlined /> Back
        </button>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <p className="text-red-800 dark:text-red-200">{error || 'Service not found'}</p>
        </div>
      </div>
    );
  }

  const wpConfig = service.metadata?.wordpressConfig || {};
  const instanceType = wpConfig.instanceType || service.metadata?.instanceType || '-';
  const storage = wpConfig.volumeGb || service.metadata?.volumeGB || '-';
  const region = service.region || wpConfig.region || '-';
  const publicIp = service.publicIp || service.metadata?.publicIp;
  const siteUrl = service.endpointUrl || service.fqdn ? `https://${service.fqdn}` : service.endpointUrl || null;
  const adminUrl = service.endpointUrlAdmin || null;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      <DetailPageHeader
        backLabel="Back to WordPress Services"
        onBack={() => navigate('/wordpress')}
        title={service.name}
        subtitle={`WordPress • ${instanceType} • ${region}`}
        icon={<ServiceLogo category="WORDPRESS" />}
        actions={
          <>
            {service.status === 'running' && (
              <HeaderActionButton variant="primary" icon={<CodeOutlined />} onClick={handleConnect}>Connect</HeaderActionButton>
            )}
            {service.status === 'stopped' && (
              <HeaderActionButton variant="success" icon={<PlayCircleOutlined />} onClick={handleStart} disabled={isProcessing}>Start</HeaderActionButton>
            )}
            {service.status === 'running' && (
              <>
                <HeaderActionButton variant="secondary" icon={<PauseCircleOutlined />} onClick={handleStop} disabled={isProcessing}>Stop</HeaderActionButton>
                <HeaderActionButton variant="secondary" icon={<SyncOutlined />} onClick={handleRestart} disabled={isProcessing}>Restart</HeaderActionButton>
              </>
            )}
            <HeaderActionMenu items={[
              { key: 'refresh', label: 'Refresh', icon: <ReloadOutlined />, onClick: () => handleRefresh(), disabled: isRefreshing },
              { key: 'credentials', label: 'Credentials', icon: <KeyOutlined />, onClick: () => handleOpenCredentialsModal() },
              { key: 'delete', label: 'Delete', icon: <DeleteOutlined />, onClick: () => setShowDeleteModal(true), danger: true, disabled: isProcessing },
            ]} />
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          {/* Overview Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Overview</h2>
              <ServiceStatusBadge status={service.status} size="md" showLabel />
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Service ID</label>
                <div className="flex items-center gap-2">
                  <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white">{service.id}</code>
                  <button onClick={() => copyText(service.id, 'Service ID')} className="p-1 text-gray-400 hover:text-gray-600"><CopyOutlined /></button>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Instance Type</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{instanceType}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Storage</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{storage} GB</p>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Region</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{region}</p>
              </div>
              {publicIp && (
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Public IP</label>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white">{publicIp}</code>
                    <button onClick={() => copyText(publicIp, 'IP')} className="p-1 text-gray-400 hover:text-gray-600"><CopyOutlined /></button>
                  </div>
                </div>
              )}
              {siteUrl && (
                <div className="sm:col-span-2">
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Site URL</label>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded font-mono text-gray-900 dark:text-white flex-1 truncate">{siteUrl}</code>
                    <button onClick={() => copyText(siteUrl, 'Site URL')} className="p-1 text-gray-400 hover:text-gray-600"><CopyOutlined /></button>
                    <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors">Open</a>
                  </div>
                </div>
              )}
              {adminUrl && (
                <div className="sm:col-span-2">
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Admin Panel (wp-admin)</label>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded font-mono text-gray-900 dark:text-white flex-1 truncate">{adminUrl}</code>
                    <button onClick={() => copyText(adminUrl, 'Admin URL')} className="p-1 text-gray-400 hover:text-gray-600"><CopyOutlined /></button>
                    <a href={adminUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors">Open</a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="border-b border-gray-200 dark:border-gray-700 flex gap-1 px-6 overflow-x-auto">
              {(['overview', 'connection', 'events', 'monitoring', 'security', 'domains'] as const).map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${activeTab === tab ? 'border-black dark:border-white text-gray-900 dark:text-white' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                  {tab}
                </button>
              ))}
            </div>

            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  <MachineSpecsCard service={service} />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Service Configuration</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Admin Panel</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">/wp-admin/ (Port 80)</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Stack</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">PHP 8.3 + MySQL 8 + Nginx</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">MySQL</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Local (port 3306)</p>
                    </div>
                    {wpConfig.adminEmail && (
                      <div>
                        <label className="text-sm text-gray-500 dark:text-gray-400">Admin Email</label>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{wpConfig.adminEmail}</p>
                      </div>
                    )}
                  </div>
                  {service.status === 'running' && (siteUrl || adminUrl) && (
                    <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-start gap-3">
                        <InfoCircleOutlined className="text-green-600 dark:text-green-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-800 dark:text-green-200">WordPress is running and ready to use.</p>
                          <div className="mt-3 flex gap-4 flex-wrap">
                            {siteUrl && (
                              <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-green-700 dark:text-green-300 hover:underline">
                                <GlobalOutlined /> Open Site
                              </a>
                            )}
                            {adminUrl && (
                              <a href={adminUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-green-700 dark:text-green-300 hover:underline">
                                <GlobalOutlined /> Open wp-admin
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'connection' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Connection Information</h3>
                  {!publicIp ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">Connection info not available yet. Wait for the service to finish provisioning.</p>
                    </div>
                  ) : (
                    <>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">WordPress</h4>
                        <div className="space-y-3">
                          {siteUrl && (
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400">Site URL</label>
                              <div className="flex items-center gap-2 mt-0.5">
                                <code className="text-sm bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded font-mono text-gray-900 dark:text-white flex-1">{siteUrl}</code>
                                <button onClick={() => copyText(siteUrl, 'Site URL')} className="p-1.5 text-gray-400 hover:text-gray-600"><CopyOutlined /></button>
                              </div>
                            </div>
                          )}
                          {adminUrl && (
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400">Admin Panel</label>
                              <div className="flex items-center gap-2 mt-0.5">
                                <code className="text-sm bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded font-mono text-gray-900 dark:text-white flex-1">{adminUrl}</code>
                                <button onClick={() => copyText(adminUrl, 'Admin URL')} className="p-1.5 text-gray-400 hover:text-gray-600"><CopyOutlined /></button>
                                <a href={adminUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded">Open</a>
                              </div>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400">Port</label>
                              <p className="text-sm font-mono text-gray-900 dark:text-white mt-0.5">80 (HTTP via Nginx)</p>
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400">Credentials</label>
                              <button onClick={handleOpenCredentialsModal} className="text-sm text-blue-600 dark:text-blue-400 hover:underline mt-0.5 flex items-center gap-1">
                                <KeyOutlined className="text-xs" /> View credentials
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Open Ports</h4>
                        <div className="space-y-2">
                          {[
                            { port: '80', protocol: 'HTTP', desc: 'WordPress (via Nginx)' },
                            { port: '3306', protocol: 'MySQL', desc: 'Internal only' },
                          ].map((p) => (
                            <div key={p.port} className="flex items-center justify-between py-1.5">
                              <div className="flex items-center gap-3">
                                <code className="text-sm font-mono font-semibold text-gray-900 dark:text-white w-14">{p.port}</code>
                                <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-400">{p.protocol}</span>
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400">{p.desc}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'events' && <LazyServiceEventsList serviceId={id || ''} eventsPerPage={10} />}
              {activeTab === 'monitoring' && <MonitoringTab service={service} />}
              {activeTab === 'security' && (
                <SecurityRulesTab
                  serviceId={id || ''}
                  securityRules={service.securityRules}
                  securityGroupId={service.securityGroupId}
                />
              )}
              {activeTab === 'domains' && (
                <CustomDomainsTab serviceId={id || ''} fqdn={service.fqdn} />
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Status</h3>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
                <ServiceStatusBadge status={service.status} />
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Created</span>
                <span className="text-sm text-gray-900 dark:text-white">{getServiceAge(service.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Updated</span>
                <span className="text-sm text-gray-900 dark:text-white">{getServiceAge(service.updatedAt)}</span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Quick Actions</h3>
            </div>
            <div className="p-6 space-y-3">
              {service.status === 'running' && (
                <>
                  <button onClick={handleConnect} className="w-full px-4 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors flex items-center justify-center gap-2">
                    <CodeOutlined /> Connect Terminal
                  </button>
                  {siteUrl && (
                    <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                      <GlobalOutlined /> Open Site
                    </a>
                  )}
                  {adminUrl && (
                    <a href={adminUrl} target="_blank" rel="noopener noreferrer" className="w-full px-4 py-2.5 border border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 rounded-lg font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center justify-center gap-2">
                      <GlobalOutlined /> Open wp-admin
                    </a>
                  )}
                </>
              )}
              <button onClick={handleOpenCredentialsModal} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2">
                <KeyOutlined /> View Credentials
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Credentials Modal */}
      {credentialsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">WordPress Credentials</h3>
              <button onClick={() => setCredentialsModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl leading-none">&times;</button>
            </div>
            <div className="p-6 space-y-4">
              {!adminCredentials && !dbCredentials ? (
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Credentials are stored in AWS Secrets Manager. Click to retrieve them.</p>
                  <button onClick={handleFetchCredentials} disabled={loadingCreds} className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-2 mx-auto">
                    {loadingCreds ? <><LoadingOutlined className="animate-spin" /> Retrieving...</> : <><KeyOutlined /> Retrieve Credentials</>}
                  </button>
                </div>
              ) : (
                <>
                  {adminCredentials && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Admin Account</h4>
                      {adminCredentials.email && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">Email</label>
                          <div className="flex items-center gap-2 mt-0.5">
                            <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white flex-1">{adminCredentials.email}</code>
                            <button onClick={() => copyText(adminCredentials.email!, 'Email')} className="p-1 text-gray-400 hover:text-gray-600"><CopyOutlined /></button>
                          </div>
                        </div>
                      )}
                      {adminCredentials.password && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">Password</label>
                          <div className="flex items-center gap-2 mt-0.5">
                            <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white flex-1">{adminPasswordVisible ? adminCredentials.password : '••••••••••••'}</code>
                            <button onClick={() => setAdminPasswordVisible(!adminPasswordVisible)} className="p-1 text-gray-400 hover:text-gray-600">{adminPasswordVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}</button>
                            <button onClick={() => copyText(adminCredentials.password!, 'Password')} className="p-1 text-gray-400 hover:text-gray-600"><CopyOutlined /></button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {dbCredentials && (
                    <div className="space-y-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">MySQL Database</h4>
                      {dbCredentials.username && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">Username</label>
                          <div className="flex items-center gap-2 mt-0.5">
                            <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white flex-1">{dbCredentials.username}</code>
                            <button onClick={() => copyText(dbCredentials.username!, 'Username')} className="p-1 text-gray-400 hover:text-gray-600"><CopyOutlined /></button>
                          </div>
                        </div>
                      )}
                      {dbCredentials.password && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">Password</label>
                          <div className="flex items-center gap-2 mt-0.5">
                            <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white flex-1">{dbPasswordVisible ? dbCredentials.password : '••••••••••••'}</code>
                            <button onClick={() => setDbPasswordVisible(!dbPasswordVisible)} className="p-1 text-gray-400 hover:text-gray-600">{dbPasswordVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}</button>
                            <button onClick={() => copyText(dbCredentials.password!, 'DB Password')} className="p-1 text-gray-400 hover:text-gray-600"><CopyOutlined /></button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete WordPress Service"
        message={`Are you sure you want to delete "${service.name}"? This will terminate the EC2 instance and remove all data permanently.`}
        confirmText="Delete Service"
        loading={isDeleting}
        variant="danger"
      />
    </div>
  );
};

export default WordPressDetail;
